"use client";

import { useState } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { MessageCircle, Mail, Send, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/shadcn-components/ui/button";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import { Textarea } from "@/shadcn-components/ui/textarea";
import { Input } from "@/shadcn-components/ui/input";
import { Label } from "@/shadcn-components/ui/label";
import { Separator } from "@/shadcn-components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shadcn-components/ui/dialog";

const DEVELOPER_EMAIL = "developer@vybetrader.com";

interface ContactDeveloperScreenProps {
  onBack: () => void;
}

export default function ContactDeveloperScreen({ onBack }: ContactDeveloperScreenProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const resetForm = () => {
    setEmail("");
    setSubject("");
    setMessage("");
  };

  const handleSend = () => {
    if (!email.trim() || !subject.trim() || !message.trim()) return;
    setDialogOpen(false);
    resetForm();
    toast.success("Message sent");
  };

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 h-14 bg-background border-b border-border">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-10 w-10">
          <FaChevronLeft className="text-blue-400 text-xl" />
        </Button>
        <h1 className="text-xl font-medium text-foreground">Contact Developer</h1>
        <div className="w-10" />
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="flex items-center gap-3 mb-6 px-1">
          <div className="w-10 h-10 rounded-lg bg-indigo-500 flex items-center justify-center">
            <MessageCircle className="h-5 w-5 text-white" />
          </div>
          <p className="text-sm text-muted-foreground">
            Get help or send feedback to the VybeTrader team.
          </p>
        </div>

        <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
          QUICK CONTACT
        </h2>
        <Card>
          <CardContent className="p-0">
            <Button
              variant="ghost"
              onClick={() => window.open(`mailto:${DEVELOPER_EMAIL}`, "_blank")}
              className="w-full h-auto p-3 justify-start hover:bg-muted/50 rounded-none rounded-t-lg"
            >
              <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center mr-3">
                <Mail className="text-white text-base" />
              </div>
              <div className="flex-1 text-left min-w-0 mr-2">
                <div className="text-sm font-medium">Email Direct</div>
                <div className="text-xs text-muted-foreground">
                  {DEVELOPER_EMAIL}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </Button>

            <Separator />

            <Button
              variant="ghost"
              onClick={() => setDialogOpen(true)}
              className="w-full h-auto p-3 justify-start hover:bg-muted/50 rounded-none rounded-b-lg"
            >
              <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center mr-3">
                <Send className="text-white text-base" />
              </div>
              <div className="flex-1 text-left min-w-0 mr-2">
                <div className="text-sm font-medium">Send Message</div>
                <div className="text-xs text-muted-foreground">
                  Compose a message to the developer
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </Button>
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Send Message</DialogTitle>
            <DialogDescription>
              Your message will be reviewed by the VybeTrader team.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="contact-email-mobile">Your Email</Label>
              <Input
                id="contact-email-mobile"
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-subject-mobile">Subject</Label>
              <Input
                id="contact-subject-mobile"
                placeholder="What's this about?"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-message-mobile">Message</Label>
              <Textarea
                id="contact-message-mobile"
                placeholder="Describe your issue, feedback, or question..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={!email.trim() || !subject.trim() || !message.trim()}
            >
              <Send className="mr-2 h-4 w-4" />
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
